import { type ReactNode, type CSSProperties, forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { Icons } from '@/theme';
import {
  tableContainer, tableRoot, tableHead, tableBody, tableRow,
  tableCell, tableCellSmall, tableCellHead,
  tableSortLabel, tablePaginationRoot, tablePaginationButton, tablePaginationSelect,
} from './table.css';

// ============================================================
// TableContainer
// ============================================================

export type TableContainerProps = HTMLAttributes<HTMLDivElement>;

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(tableContainer, className)} {...props}>
      {children}
    </div>
  ),
);
TableContainer.displayName = 'TableContainer';

// ============================================================
// Table
// ============================================================

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  size?: 'small' | 'medium';
  stickyHeader?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ size = 'medium', stickyHeader = false, className, children, ...props }, ref) => (
    // F17: previously size and stickyHeader were destructured into dead
    // unused vars. Surface them as data attributes so callers (and CSS) can
    // observe and react to them without changing the public API.
    <table
      ref={ref}
      className={clsx(tableRoot, className)}
      data-size={size}
      data-sticky-header={stickyHeader ? 'true' : undefined}
      {...props}
    >
      {children}
    </table>
  ),
);
Table.displayName = 'Table';

// ============================================================
// TableHead
// ============================================================

export type TableHeadProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, children, ...props }, ref) => (
    <thead ref={ref} className={clsx(tableHead, className)} {...props}>
      {children}
    </thead>
  ),
);
TableHead.displayName = 'TableHead';

// ============================================================
// TableBody
// ============================================================

export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement>;

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <tbody ref={ref} className={clsx(tableBody, className)} {...props}>
      {children}
    </tbody>
  ),
);
TableBody.displayName = 'TableBody';

// ============================================================
// TableRow
// ============================================================

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  hover?: boolean;
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ hover = false, selected = false, className, children, ...props }, ref) => (
    <tr
      ref={ref}
      data-hover={hover || undefined}
      data-selected={selected || undefined}
      className={clsx(tableRow, className)}
      {...props}
    >
      {children}
    </tr>
  ),
);
TableRow.displayName = 'TableRow';

// ============================================================
// TableCell
// ============================================================

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  header?: boolean;
  size?: 'small' | 'medium';
  align?: 'left' | 'center' | 'right';
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ header = false, size, align, className, children, ...props }, ref) => {
    const cls = clsx(
      tableCell,
      size === 'small' && tableCellSmall,
      header && tableCellHead,
      className,
    );
    const style = align && align !== 'left' ? { textAlign: align } : undefined;

    return header ? (
      <th ref={ref as React.Ref<HTMLTableCellElement>} className={cls} style={style} {...(props as ThHTMLAttributes<HTMLTableCellElement>)}>
        {children}
      </th>
    ) : (
      <td ref={ref} className={cls} style={style} {...props}>
        {children}
      </td>
    );
  },
);
TableCell.displayName = 'TableCell';

// ============================================================
// TableSortLabel
// ============================================================

export interface TableSortLabelProps {
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function TableSortLabel({ active, direction, onClick, children, className, style }: TableSortLabelProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(tableSortLabel, className)}
      style={style}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {children}
      {active && (
        direction === 'asc'
          ? <Icons.expandLess style={{ fontSize: 14 }} />
          : <Icons.expandMore style={{ fontSize: 14 }} />
      )}
    </button>
  );
}

// ============================================================
// TablePagination
// ============================================================

export interface TablePaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<Element> | null, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  rowsPerPageOptions?: number[];
  component?: string;
  className?: string;
  style?: CSSProperties;
}

export function TablePagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
  className,
  style,
}: TablePaginationProps) {
  const totalPages = Math.ceil(count / rowsPerPage);
  const start = page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, count);

  return (
    <div className={clsx(tablePaginationRoot, className)} style={style}>
      <span>Rows per page:</span>
      <select
        className={tablePaginationSelect}
        value={rowsPerPage}
        onChange={onRowsPerPageChange as unknown as React.ChangeEventHandler<HTMLSelectElement>}
      >
        {rowsPerPageOptions.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
      <span>{count === 0 ? '0–0 of 0' : `${start}–${end} of ${count}`}</span>
      <button
        type="button"
        className={tablePaginationButton}
        onClick={(e) => onPageChange(e, page - 1)}
        disabled={page === 0}
        aria-label="Previous page"
      >
        <Icons.back style={{ fontSize: 16 }} />
      </button>
      <button
        type="button"
        className={tablePaginationButton}
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= totalPages - 1}
        aria-label="Next page"
      >
        <Icons.forward style={{ fontSize: 16 }} />
      </button>
    </div>
  );
}
